import { useEffect, useState, useMemo, useContext } from 'react';
import Tooltip from "../../components/common/Tooltip";
import Location from "../../hooks/Location";
import defaultDates from '../../utils/defaultDates';
import useSettings from '../../hooks/Settings';
import useHierarchyData from '../../hooks/useHierarchyData';
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import AnalysisItemNavigator from '../analysis/AnalysisItemNavigator';
import TotalResponses from '../Visualisations/TotalResponses';
import AnalyticsEmptyState from '../dashboard/AnalyticsEmptyState';
import LiveReportRenderContext from '../../StateManagment/LiveReportRenderContext';
import OpenAnswersList from './OpenAnswersList';
import spinner from '../../images/spinner-ripple.svg';

// AI-CONTEXT: See docs/features/api-data-fetching.md

const OpenQuestionSlider = ({ researchId, field, startDate, endDate, personaId, filterByResearchId, onTotalResponsesChange }) => {
  const [visibleInGroup, setVisibleInGroup] = useState(0);
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);
  const [printPage, setPrintPage] = useState(false);

  const page = Location()[2];
  const inLiveReport = useContext(LiveReportRenderContext);
  const { portfolio } = useSettings();

  const researchIds = useMemo(
    () => Array.isArray(researchId) ? researchId.filter(Boolean) : researchId ? [researchId] : [],
    [researchId]
  );

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  // Fetch records via Cloud Function
  const { records, hierarchy: projectHierarchy, totalRespondents, loading, error } = useHierarchyData({
    syncType: 'Question',
    id: field,
    startDate: startMs,
    endDate: endMs,
    researchIds,
    personaId,
    filterByResearchId,
  });

  useEffect(() => {
    onTotalResponsesChange?.(totalRespondents);
  }, [totalRespondents, onTotalResponsesChange]);

  useEffect(() => {
    setPrintPage(page === 'printlivereport');
  }, [page]);

  // Group responses by MeasureMoment
  const groups = useMemo(() => {
    if (!records?.length) return [];

    const map = new Map();

    records.forEach(r => {
      const posRaw = r.MomentPosition;
      const posNum = (posRaw !== undefined && posRaw !== null && !isNaN(Number(posRaw)))
        ? Number(posRaw)
        : null;

      const key = posNum !== null ? `pos_${posNum}` : 'unknown';
      const name = posNum !== null ? `Meetmoment ${posNum}` : 'Onbekend moment';

      if (!map.has(key)) map.set(key, { position: posNum, name, items: [] });
      map.get(key).items.push(r);
    });

    for (const g of map.values()) {
      g.items.sort((a, b) => {
        const ta = a.Timestamp?.toMillis?.() ?? (a.Timestamp?.seconds ? a.Timestamp.seconds * 1000 : 0);
        const tb = b.Timestamp?.toMillis?.() ?? (b.Timestamp?.seconds ? b.Timestamp.seconds * 1000 : 0);
        return ta - tb;
      });
    }

    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      const aPos = a.position ?? Infinity;
      const bPos = b.position ?? Infinity;
      return aPos - bPos;
    });

    return arr;
  }, [records]);

  useEffect(() => {
    setActiveGroupIdx(0);
    setVisibleInGroup(0);
  }, [groups.length]);

  useEffect(() => {
    setVisibleInGroup(0);
  }, [activeGroupIdx]);

  const currentGroup = groups[activeGroupIdx] || { name: '', position: null, items: [] };
  const currentItems = currentGroup.items || [];
  const hasData = groups.length > 0 && currentItems.length > 0;

  const projectResults = () => {
    if (!projectHierarchy) return null;

    const sortedProjects = Object.entries(projectHierarchy).sort(
      ([, a], [, b]) => (a?.path || '').localeCompare(b?.path || '')
    );

    return (
      <div className="scale-visualisation-project-results-container" style={{ whiteSpace: 'nowrap' }}>
        {sortedProjects.map(([name, info], idx) => (
          <div key={idx} style={{ paddingLeft: `${info.depth * 10}px` }}>
            <p>{name ?? '–'}</p>
          </div>
        ))}
      </div>
    );
  };

  if (printPage) {
    return (
      <div id="live-report-open-question-slider">
        {groups.length === 0 ? (
          <AnalyticsEmptyState />
        ) : (
          groups.map((g, gi) => (
            <div key={`group-${gi}`} className="open-question-group">
              <h3 className="measure-moment-title">
                {g.name}{g.position !== null && <> (Positie {g.position})</>}
              </h3>
              {g.items.map((r, i) => (
                <div key={`g-${gi}-item-${i}`} className="slide">
                  <p>{i + 1}</p>
                  <p>{r.Input}</p>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    );
  }

  // In een live rapportage alle antwoorden onder elkaar: de lezer van een
  // gedeelde rapportage hoort niet door te hoeven klikken.
  if (inLiveReport) {
    if (loading) return <img src={spinner} alt="loading" />;
    // Een mislukte lees geeft een lege lijst; zonder deze tak stond er 'geen
    // gegevens' terwijl de antwoorden er wel zijn.
    // AI-CONTEXT: See docs/pitfalls.md#api-fallback-collapses-failure-into-empty
    if (error) return <p className="graph-data-error">De antwoorden konden niet worden geladen.</p>;
    if (groups.length === 0) return <AnalyticsEmptyState />;
    return <OpenAnswersList groups={groups} totalResponses={totalRespondents} />;
  }

  return (
    <>
      {hasData ? (
        <div id="text-reponses-container">
          <TotalResponses totalResponses={totalRespondents} />

          {/* MeasureMoment navbar */}
          <div id='live-report-measuremoment-header'>
            <h2>Meetmomenten</h2>
            <AnalysisItemNavigator
              items={groups}
              visible={activeGroupIdx}
              setVisible={setActiveGroupIdx}
            />
            <h3>{currentGroup.name}</h3>
          </div>

          <div id="live-report-open-question-slider">
            <Tooltip content={projectResults()} top='-100px' width={'100%'}>
              <p id='open-question-response-p-tag'>
                {currentItems[visibleInGroup]?.Input}
              </p>
            </Tooltip>
          </div>

          <AnalysisItemNavigator
            items={currentItems}
            visible={visibleInGroup}
            setVisible={setVisibleInGroup}
          />
        </div>
      ) : (
        <AnalyticsEmptyState />
      )}
    </>
  );
};

export default OpenQuestionSlider;